import { supabase } from '../../../lib/supabase';
import { safeStorage } from '../../../utils/safeStorage';
import {
  TransacaoFinanceira,
  ContaBancaria,
  CategoriaFinanceira,
  DashboardFinanceiroKPIs,
  FiltroTransacoes,
  BaixaTituloDTO
} from '../types';

const STORAGE_KEYS = {
  TRANSACOES: 'rmax_financeiro_transacoes_v1',
  CONTAS: 'rmax_financeiro_contas_v1',
  CATEGORIAS: 'rmax_financeiro_categorias_v1'
};

const DEFAULT_CONTAS: ContaBancaria[] = [
  {
    id: 'conta_padrao_caixa',
    empresaId: 'default',
    nome: 'Caixa Geral / Balcão',
    saldoInicial: 0.00,
    saldoAtual: 0.00,
    ativo: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 'conta_padrao_banco',
    empresaId: 'default',
    nome: 'Conta Bancária Principal',
    agencia: '0001',
    conta: '12345-6',
    saldoInicial: 1500.00,
    saldoAtual: 1500.00,
    ativo: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

const DEFAULT_CATEGORIAS: CategoriaFinanceira[] = [
  { id: 'cat_rec_vendas', empresaId: 'default', nome: 'Venda de Produtos', tipo: 'RECEITA' },
  { id: 'cat_rec_servicos', empresaId: 'default', nome: 'Prestação de Serviços', tipo: 'RECEITA' },
  { id: 'cat_rec_rendimentos', empresaId: 'default', nome: 'Receitas Financeiras', tipo: 'RECEITA' },
  { id: 'cat_desp_fornecedores', empresaId: 'default', nome: 'Fornecedores e Insumos', tipo: 'DESPESA' },
  { id: 'cat_desp_aluguel', empresaId: 'default', nome: 'Aluguel e Condomínio', tipo: 'DESPESA' },
  { id: 'cat_desp_energia', empresaId: 'default', nome: 'Energia, Água e Internet', tipo: 'DESPESA' },
  { id: 'cat_desp_folha', empresaId: 'default', nome: 'Salários e Pró-Labore', tipo: 'DESPESA' },
  { id: 'cat_desp_impostos', empresaId: 'default', nome: 'Impostos e Tributos', tipo: 'DESPESA' },
  { id: 'cat_desp_manutencao', empresaId: 'default', nome: 'Manutenção e Equipamentos', tipo: 'DESPESA' },
  { id: 'cat_desp_outros', empresaId: 'default', nome: 'Outras Despesas Operacionais', tipo: 'DESPESA' }
];

export class FinanceiroClientService {
  /**
   * Helper para carregar cache local (Offline-First)
   */
  private static getLocalTransactions(empresaId: string): TransacaoFinanceira[] {
    const raw = safeStorage.getItem(`${STORAGE_KEYS.TRANSACOES}_${empresaId}`);
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }

  private static saveLocalTransactions(empresaId: string, items: TransacaoFinanceira[]): void {
    safeStorage.setItem(`${STORAGE_KEYS.TRANSACOES}_${empresaId}`, JSON.stringify(items));
  }

  /**
   * Listar Contas Bancárias (Supabase com fallback LocalStorage)
   */
  static async listarContasBancarias(empresaId: string): Promise<ContaBancaria[]> {
    try {
      const { data, error } = await supabase
        .from('contas_bancarias')
        .select('*')
        .eq('empresa_id', empresaId)
        .is('deleted_at', null)
        .order('nome', { ascending: true });

      if (!error && data && data.length > 0) {
        const mapped = data.map((c: any) => ({
          id: c.id,
          empresaId: c.empresa_id,
          nome: c.nome,
          agencia: c.agencia,
          conta: c.conta,
          saldoInicial: Number(c.saldo_inicial || 0),
          saldoAtual: Number(c.saldo_inicial || 0),
          ativo: c.ativo,
          createdAt: c.created_at,
          updatedAt: c.updated_at
        }));
        safeStorage.setItem(`${STORAGE_KEYS.CONTAS}_${empresaId}`, JSON.stringify(mapped));
        return mapped;
      }
    } catch (e) {
      console.warn('Fallback para contas locais:', e);
    }

    // Fallback Offline
    const cached = safeStorage.getItem(`${STORAGE_KEYS.CONTAS}_${empresaId}`);
    if (cached) {
      try { return JSON.parse(cached); } catch { /* ignore */ }
    }

    const initial = DEFAULT_CONTAS.map(c => ({ ...c, empresaId }));
    safeStorage.setItem(`${STORAGE_KEYS.CONTAS}_${empresaId}`, JSON.stringify(initial));
    return initial;
  }

  /**
   * Salvar ou Atualizar Conta Bancária
   */
  static async salvarContaBancaria(empresaId: string, conta: Partial<ContaBancaria>): Promise<ContaBancaria> {
    const id = conta.id || `conta_${Date.now()}`;
    const payload = {
      id,
      empresaId,
      nome: conta.nome || 'Novo Caixa',
      agencia: conta.agencia || null,
      conta: conta.conta || null,
      saldoInicial: conta.saldoInicial || 0,
      saldoAtual: conta.saldoAtual || conta.saldoInicial || 0,
      ativo: conta.ativo ?? true,
      createdAt: conta.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Tenta Supabase
    try {
      await supabase
        .from('contas_bancarias')
        .upsert({
          id: payload.id.includes('conta_') ? undefined : payload.id,
          empresa_id: empresaId,
          nome: payload.nome,
          agencia: payload.agencia,
          conta: payload.conta,
          saldo_inicial: payload.saldoInicial,
          ativo: payload.ativo
        });
    } catch (e) {
      console.warn('Erro ao salvar conta no Supabase, salvando offline:', e);
    }

    const contas = await this.listarContasBancarias(empresaId);
    const updated = contas.filter(c => c.id !== id);
    updated.push(payload);
    safeStorage.setItem(`${STORAGE_KEYS.CONTAS}_${empresaId}`, JSON.stringify(updated));
    return payload;
  }

  /**
   * Listar Categorias Financeiras
   */
  static async listarCategorias(empresaId: string): Promise<CategoriaFinanceira[]> {
    try {
      const { data, error } = await supabase
        .from('categorias_financeiras')
        .select('*')
        .eq('empresa_id', empresaId)
        .is('deleted_at', null)
        .order('nome', { ascending: true });

      if (!error && data && data.length > 0) {
        const mapped = data.map((c: any) => ({
          id: c.id,
          empresaId: c.empresa_id,
          nome: c.nome,
          tipo: c.tipo,
          categoriaPaiId: c.categoria_pai_id,
          createdAt: c.created_at,
          updatedAt: c.updated_at
        }));
        safeStorage.setItem(`${STORAGE_KEYS.CATEGORIAS}_${empresaId}`, JSON.stringify(mapped));
        return mapped;
      }
    } catch (e) {
      console.warn('Fallback para categorias locais:', e);
    }

    const cached = safeStorage.getItem(`${STORAGE_KEYS.CATEGORIAS}_${empresaId}`);
    if (cached) {
      try { return JSON.parse(cached); } catch { /* ignore */ }
    }

    const initial = DEFAULT_CATEGORIAS.map(c => ({ ...c, empresaId }));
    safeStorage.setItem(`${STORAGE_KEYS.CATEGORIAS}_${empresaId}`, JSON.stringify(initial));
    return initial;
  }

  /**
   * Salvar Categoria Financeira
   */
  static async salvarCategoria(empresaId: string, cat: Partial<CategoriaFinanceira>): Promise<CategoriaFinanceira> {
    const id = cat.id || `cat_${Date.now()}`;
    const payload: CategoriaFinanceira = {
      id,
      empresaId,
      nome: cat.nome || 'Nova Categoria',
      tipo: cat.tipo || 'DESPESA',
      categoriaPaiId: cat.categoriaPaiId || null,
      createdAt: cat.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      await supabase
        .from('categorias_financeiras')
        .upsert({
          id: payload.id.includes('cat_') ? undefined : payload.id,
          empresa_id: empresaId,
          nome: payload.nome,
          tipo: payload.tipo,
          categoria_pai_id: payload.categoriaPaiId
        });
    } catch (e) {
      console.warn('Erro ao salvar categoria no Supabase, mantendo offline:', e);
    }

    const cats = await this.listarCategorias(empresaId);
    const updated = cats.filter(c => c.id !== id);
    updated.push(payload);
    safeStorage.setItem(`${STORAGE_KEYS.CATEGORIAS}_${empresaId}`, JSON.stringify(updated));
    return payload;
  }

  /**
   * Listar Transações Financeiras com filtros e atualização dinâmica de títulos atrasados
   */
  static async listarTransacoes(empresaId: string, filtros: FiltroTransacoes = {}): Promise<TransacaoFinanceira[]> {
    let remoteList: TransacaoFinanceira[] | null = null;

    try {
      let query = supabase
        .from('transacoes_financeiras')
        .select(`
          *,
          categorias_financeiras:categoria_id(nome),
          contas_bancarias:conta_bancaria_id(nome)
        `)
        .eq('empresa_id', empresaId)
        .is('deleted_at', null)
        .order('data_vencimento', { ascending: true });

      if (filtros.tipo && filtros.tipo !== 'TODOS') {
        query = query.eq('tipo', filtros.tipo);
      }
      if (filtros.status && filtros.status !== 'TODOS') {
        query = query.eq('status', filtros.status);
      }

      const { data, error } = await query;
      if (!error && data) {
        const todayStr = new Date().toISOString().split('T')[0];
        remoteList = data.map((row: any) => {
          let st = row.status;
          if (st === 'PENDENTE' && row.data_vencimento < todayStr) {
            st = 'ATRASADO';
          }
          return {
            id: row.id,
            empresaId: row.empresa_id,
            tipo: row.tipo,
            descricao: row.descricao,
            valorOriginal: Number(row.valor_original),
            valorPago: Number(row.valor_pago || 0),
            dataVencimento: row.data_vencimento,
            dataPagamento: row.data_pagamento,
            status: st,
            categoriaId: row.categoria_id,
            categoriaNome: row.categorias_financeiras?.nome,
            contaBancariaId: row.conta_bancaria_id,
            contaBancariaNome: row.contas_bancarias?.nome,
            pessoaId: row.pessoa_id,
            tipoPessoa: row.tipo_pessoa,
            pessoaNome: row.pessoa_nome,
            formaPagamento: row.forma_pagamento,
            numeroDocumento: row.numero_documento,
            observacoes: row.observacoes,
            anexoUrl: row.anexo_url,
            createdAt: row.created_at,
            updatedAt: row.updated_at
          };
        });

        this.saveLocalTransactions(empresaId, remoteList);
      }
    } catch (e) {
      console.warn('Fallback offline para transações financeiras:', e);
    }

    const items = remoteList || this.getLocalTransactions(empresaId);
    const todayStr = new Date().toISOString().split('T')[0];

    // Aplicar filtros e ordenação
    return items
      .map(item => {
        let st = item.status;
        if (st === 'PENDENTE' && item.dataVencimento < todayStr) {
          st = 'ATRASADO';
        }
        return { ...item, status: st };
      })
      .filter(item => {
        if (item.deletedAt) return false;
        if (filtros.tipo && filtros.tipo !== 'TODOS' && item.tipo !== filtros.tipo) return false;
        if (filtros.status && filtros.status !== 'TODOS' && item.status !== filtros.status) return false;
        if (filtros.categoriaId && item.categoriaId !== filtros.categoriaId) return false;
        if (filtros.contaBancariaId && item.contaBancariaId !== filtros.contaBancariaId) return false;
        if (filtros.busca) {
          const q = filtros.busca.toLowerCase();
          const matches =
            item.descricao.toLowerCase().includes(q) ||
            (item.pessoaNome && item.pessoaNome.toLowerCase().includes(q)) ||
            (item.numeroDocumento && item.numeroDocumento.toLowerCase().includes(q)) ||
            (item.categoriaNome && item.categoriaNome.toLowerCase().includes(q));
          if (!matches) return false;
        }
        return true;
      });
  }

  /**
   * Salvar ou Criar Título
   */
  static async salvarTransacao(empresaId: string, transacao: Partial<TransacaoFinanceira>): Promise<TransacaoFinanceira> {
    const isNew = !transacao.id;
    const id = transacao.id || `trans_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

    const payload: TransacaoFinanceira = {
      id,
      empresaId,
      tipo: transacao.tipo || 'DESPESA',
      descricao: (transacao.descricao || 'Sem Descrição').trim(),
      valorOriginal: Number(transacao.valorOriginal) || 0,
      valorPago: Number(transacao.valorPago) || 0,
      dataVencimento: transacao.dataVencimento || new Date().toISOString().split('T')[0],
      dataPagamento: transacao.dataPagamento || null,
      status: transacao.status || 'PENDENTE',
      categoriaId: transacao.categoriaId || '',
      categoriaNome: transacao.categoriaNome,
      contaBancariaId: transacao.contaBancariaId || '',
      contaBancariaNome: transacao.contaBancariaNome,
      pessoaId: transacao.pessoaId || null,
      tipoPessoa: transacao.tipoPessoa || null,
      pessoaNome: transacao.pessoaNome || null,
      formaPagamento: transacao.formaPagamento || 'PIX',
      numeroDocumento: transacao.numeroDocumento || null,
      observacoes: transacao.observacoes || null,
      anexoUrl: transacao.anexoUrl || null,
      createdAt: transacao.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Tenta persistir no Supabase se id não for sintético ou na inserção
    try {
      await supabase
        .from('transacoes_financeiras')
        .upsert({
          id: id.startsWith('trans_') ? undefined : id,
          empresa_id: empresaId,
          tipo: payload.tipo,
          descricao: payload.descricao,
          valor_original: payload.valorOriginal,
          valor_pago: payload.valorPago,
          data_vencimento: payload.dataVencimento,
          data_pagamento: payload.dataPagamento,
          status: payload.status,
          categoria_id: payload.categoriaId,
          conta_bancaria_id: payload.contaBancariaId,
          pessoa_id: payload.pessoaId,
          tipo_pessoa: payload.tipoPessoa,
          pessoa_nome: payload.pessoaNome,
          forma_pagamento: payload.formaPagamento,
          numero_documento: payload.numeroDocumento,
          observacoes: payload.observacoes,
          anexo_url: payload.anexoUrl
        });
    } catch (e) {
      console.warn('Erro ao salvar no Supabase, salvando localmente:', e);
    }

    const current = this.getLocalTransactions(empresaId);
    const updated = current.filter(t => t.id !== id);
    updated.unshift(payload);
    this.saveLocalTransactions(empresaId, updated);

    return payload;
  }

  /**
   * Baixar Título (Quitação com juros/desconto e data)
   */
  static async baixarTitulo(empresaId: string, id: string, dto: BaixaTituloDTO): Promise<TransacaoFinanceira> {
    const current = this.getLocalTransactions(empresaId);
    const index = current.findIndex(t => t.id === id);

    if (index === -1) {
      throw new Error('Título não encontrado no sistema.');
    }

    const target = current[index];
    const updated: TransacaoFinanceira = {
      ...target,
      status: 'PAGO',
      valorPago: Number(dto.valorPago),
      dataPagamento: dto.dataPagamento || new Date().toISOString().split('T')[0],
      contaBancariaId: dto.contaBancariaId || target.contaBancariaId,
      formaPagamento: dto.formaPagamento || target.formaPagamento,
      updatedAt: new Date().toISOString()
    };

    try {
      await supabase
        .from('transacoes_financeiras')
        .update({
          status: 'PAGO',
          valor_pago: updated.valorPago,
          data_pagamento: updated.dataPagamento,
          conta_bancaria_id: updated.contaBancariaId,
          forma_pagamento: updated.formaPagamento
        })
        .eq('id', id)
        .eq('empresa_id', empresaId);
    } catch (e) {
      console.warn('Erro na baixa remota, aplicando offline:', e);
    }

    current[index] = updated;
    this.saveLocalTransactions(empresaId, current);
    return updated;
  }

  /**
   * Estornar Título
   */
  static async estornarTitulo(empresaId: string, id: string): Promise<TransacaoFinanceira> {
    const current = this.getLocalTransactions(empresaId);
    const index = current.findIndex(t => t.id === id);

    if (index === -1) {
      throw new Error('Título não encontrado.');
    }

    const target = current[index];
    const todayStr = new Date().toISOString().split('T')[0];
    const newStatus = target.dataVencimento < todayStr ? 'ATRASADO' : 'PENDENTE';

    const updated: TransacaoFinanceira = {
      ...target,
      status: newStatus,
      valorPago: 0.00,
      dataPagamento: null,
      updatedAt: new Date().toISOString()
    };

    try {
      await supabase
        .from('transacoes_financeiras')
        .update({
          status: newStatus,
          valor_pago: 0.00,
          data_pagamento: null
        })
        .eq('id', id)
        .eq('empresa_id', empresaId);
    } catch (e) {
      console.warn('Erro no estorno remoto, aplicando offline:', e);
    }

    current[index] = updated;
    this.saveLocalTransactions(empresaId, current);
    return updated;
  }

  /**
   * Excluir Título (Soft Delete)
   */
  static async excluirTransacao(empresaId: string, id: string): Promise<void> {
    try {
      await supabase
        .from('transacoes_financeiras')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .eq('empresa_id', empresaId);
    } catch (e) {
      console.warn('Erro na exclusão remota, aplicando offline:', e);
    }

    const current = this.getLocalTransactions(empresaId);
    const filtered = current.filter(t => t.id !== id);
    this.saveLocalTransactions(empresaId, filtered);
  }

  /**
   * Calcular KPIs consolidados do Dashboard Financeiro
   */
  static calcularKPIs(transacoes: TransacaoFinanceira[]): DashboardFinanceiroKPIs {
    let totalReceberMes = 0;
    let totalPagarMes = 0;
    let totalRealizadoReceitas = 0;
    let totalRealizadoDespesas = 0;
    let totalAtrasado = 0;
    let quantidadeTitulosPendentes = 0;
    let quantidadeTitulosReceber = 0;
    let quantidadeTitulosPagar = 0;

    for (const t of transacoes) {
      if (t.deletedAt) continue;

      if (t.tipo === 'RECEITA') {
        quantidadeTitulosReceber++;
        totalReceberMes += t.valorOriginal;
        if (t.status === 'PAGO') {
          totalRealizadoReceitas += (t.valorPago || t.valorOriginal);
        } else if (t.status === 'ATRASADO') {
          totalAtrasado += t.valorOriginal;
          quantidadeTitulosPendentes++;
        } else if (t.status === 'PENDENTE') {
          quantidadeTitulosPendentes++;
        }
      } else {
        quantidadeTitulosPagar++;
        totalPagarMes += t.valorOriginal;
        if (t.status === 'PAGO') {
          totalRealizadoDespesas += (t.valorPago || t.valorOriginal);
        } else if (t.status === 'ATRASADO' || t.status === 'PENDENTE') {
          quantidadeTitulosPendentes++;
        }
      }
    }

    const saldoPrevistoMes = totalReceberMes - totalPagarMes;
    const saldoRealizadoCaixa = totalRealizadoReceitas - totalRealizadoDespesas;
    const taxaInadimplenciaReceber = totalReceberMes > 0 ? (totalAtrasado / totalReceberMes) * 100 : 0;

    return {
      totalReceberMes,
      totalPagarMes,
      saldoPrevistoMes,
      saldoRealizadoCaixa,
      totalAtrasado,
      taxaInadimplenciaReceber: Number(taxaInadimplenciaReceber.toFixed(1)),
      quantidadeTitulosPendentes,
      quantidadeTitulosReceber,
      quantidadeTitulosPagar
    };
  }
}
